import { useEffect, useMemo, useRef, useState } from 'react';

import { COACH_FUNCTION_OPTIONS, isCoachFunctionSlug } from '@/constants';
import { Alert, Autocomplete, Box, Button, TextField, Typography } from '@/ui';

import type { User } from '@/types';
import type { FC } from 'react';

import { useUpdateCoachProfileMutation } from '@/store/api/coachAssignmentApi';

interface EditCoachFunctionsProps {
  coach: User;
  refetchCoach: () => { unwrap: () => Promise<unknown> };
}

const EditCoachFunctions: FC<EditCoachFunctionsProps> = ({
  coach,
  refetchCoach,
}) => {
  const coachFunctions = coach.coachProfile?.functions;
  const initialFunctions = useMemo(
    () => (coachFunctions ?? []).filter(isCoachFunctionSlug),
    [coachFunctions],
  );
  const unknownFunctions = useMemo(
    () =>
      (coachFunctions ?? []).filter(
        (coachFunction) => !isCoachFunctionSlug(coachFunction),
      ),
    [coachFunctions],
  );
  const [functions, setFunctions] = useState(initialFunctions);
  const loadedCoachIdRef = useRef(coach.id);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [updateCoachProfile] = useUpdateCoachProfileMutation();

  useEffect(() => {
    if (loadedCoachIdRef.current !== coach.id) {
      loadedCoachIdRef.current = coach.id;
      setFunctions(initialFunctions);
      setSaved(false);
    }
  }, [coach.id, initialFunctions]);

  const selectedOptions = useMemo(
    () =>
      COACH_FUNCTION_OPTIONS.filter((option) =>
        functions.includes(option.slug),
      ),
    [functions],
  );

  const onSave = async () => {
    setIsSaving(true);
    setSaved(false);

    try {
      await updateCoachProfile({
        coachProfile: {
          functions: [...unknownFunctions, ...functions],
        },
        id: coach.id,
      }).unwrap();
      await refetchCoach().unwrap();
      setSaved(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="max-w-2xl space-y-4">
      <Typography variant="h5">Functions</Typography>
      <Autocomplete
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.slug === value.slug}
        options={COACH_FUNCTION_OPTIONS}
        renderInput={(params) => (
          <TextField
            {...params}
            helperText="Choose all that apply"
            label="Functions"
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.slug}>
            {option.label}
          </Box>
        )}
        value={selectedOptions}
        multiple
        onChange={(_, updatedOptions) => {
          setFunctions(updatedOptions.map((option) => option.slug));
          setSaved(false);
        }}
      />
      <div className="flex items-center gap-3">
        <Button loading={isSaving} variant="contained" onClick={onSave}>
          Save
        </Button>
        {saved && <Alert severity="success">Saved.</Alert>}
      </div>
    </section>
  );
};

export default EditCoachFunctions;
